import { directSupabase as supabase } from '../../lib/supabaseClient';
import * as freeAgentApi from '../../freeagent/api';
import { resyncInvoiceToFreeAgent, type SyncResult } from '../../integrations/freeagentSync';
import { documentsService } from '../docs/services/documents.service';
import { pdfExportConfigService } from '../docs/services/pdfExportConfig.service';
import { documentTemplateService } from '../docs/services/documentTemplate.service';
import type { PdfExportConfig } from '../docs/types/pdfExportConfig.types';
import { renderAccountInvoicePdfRequest } from './accountInvoicePdf';
import { KSU_INVOICE_TEMPLATE } from './ksuInvoiceTemplate';
import { dateOnlyToIssuedAt } from '../docs/utils/invoicePaymentTerms';
import type { DirectoryAccount } from './accounts.model';
import { accountsService } from './accounts.service';
import {
  canChangeInvoiceRef,
  validateInvoiceRef,
  invoiceDueDate,
  invoiceIssueDate,
  normaliseInvoiceStatus,
  recurringPeriodIndex,
  type AccountInvoiceStatus,
  type AccountInvoiceView,
} from './accountInvoice.model';
import { money, normaliseVatTreatment, type BillingArrangement } from './arrangements.model';
import { fromDbArrangement } from './arrangements.model';

export type LoadedAccountInvoice = {
  invoice: AccountInvoiceView;
  arrangement: BillingArrangement | null;
  account: DirectoryAccount | null;
  /** Admin → PDF Export settings for the invoice's organisation. */
  config: PdfExportConfig | null;
  organisationId: string;
};

function toView(row: Record<string, any>): AccountInvoiceView {
  const vatEnabled = !!row.vat_enabled;
  const vatPct = row.vat_pct != null ? Number(row.vat_pct) : 0.2;
  const issueDate = invoiceIssueDate(row.issued_at, row.created_at);
  const terms = row.payment_terms_in_days != null ? Number(row.payment_terms_in_days) : 7;
  const lines = [...(row.document_lines || [])]
    .sort((a: any, b: any) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0))
    .map((line: any) => {
      const total = line.line_total != null ? Number(line.line_total) : Number(line.qty || 0) * Number(line.unit_price || 0);
      return {
        id: String(line.id),
        name: String(line.name || ''),
        description: line.description ?? null,
        qty: Number(line.qty ?? 1),
        net: money(line.vat_inclusive && vatEnabled ? total / (1 + vatPct) : total),
      };
    });
  const netTotal = row.net_total != null ? Number(row.net_total) : money(lines.reduce((sum, line) => sum + line.net, 0));
  const grossTotal = row.gross_total != null ? Number(row.gross_total) : netTotal;
  return {
    id: String(row.id),
    invoiceRef: String(row.invoice_ref || row.lifecycle_ref || ''),
    lifecycleRef: String(row.lifecycle_ref || ''),
    title: String(row.title || ''),
    status: normaliseInvoiceStatus(row.status),
    issueDate,
    paymentTermsInDays: terms,
    dueDate: invoiceDueDate(issueDate, terms),
    vatTreatment: normaliseVatTreatment(row.vat_treatment),
    vatPct,
    netTotal,
    vatAmount: row.vat_amount != null ? Number(row.vat_amount) : money(grossTotal - netTotal),
    grossTotal,
    cisDeduction: row.cis_deduction != null ? Number(row.cis_deduction) : null,
    cisLabourNet: row.cis_labour_net != null ? Number(row.cis_labour_net) : null,
    notes: row.notes ?? null,
    lines,
    freeagentInvoiceUrl: row.freeagent_invoice_url ?? null,
    sourceEvent: row.source_survey_event ?? null,
  };
}

async function loadRow(id: string): Promise<Record<string, any>> {
  const { data, error } = await supabase
    .from('documents')
    .select('*, document_lines(*)')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error('Invoice not found');
  if (!data.account_id) throw new Error('This invoice is not an account invoice.');
  return data;
}

/** Width ÷ height of an image, so the PDF header/footer band can match it. Null if it cannot load. */
function imageAspect(path: string | null | undefined): Promise<number | null> {
  const src = String(path || '').trim();
  if (!src || typeof Image === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    const done = (value: number | null) => resolve(value);
    img.onload = () => done(img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : null);
    img.onerror = () => done(null);
    setTimeout(() => done(null), 5000);
    img.src = src;
  });
}

/** If this is the latest recurring period, make that period due again. */
async function releaseRecurringPeriod(row: Record<string, any>): Promise<void> {
  const index = recurringPeriodIndex(row.source_survey_event);
  if (index == null) return;
  await supabase
    .from('billing_arrangements')
    .update({ next_period_index: index })
    .eq('organisation_id', row.organisation_id)
    .eq('lifecycle_ref', row.lifecycle_ref)
    .eq('next_period_index', index + 1);
}

/** PDF settings for account invoices, falling back to the brand's Invoice settings until they exist. */
async function accountInvoiceConfig(organisationId: string): Promise<PdfExportConfig | null> {
  const own = await pdfExportConfigService.getConfigByOrganisation(organisationId, 'AccountInvoice').catch(() => null);
  return own || pdfExportConfigService.getConfigByOrganisation(organisationId, 'Invoice').catch(() => null);
}

export const accountInvoicesService = {
  async load(id: string): Promise<LoadedAccountInvoice> {
    const row = await loadRow(id);
    const [arrangementResult, account, config] = await Promise.all([
      supabase
        .from('billing_arrangements')
        .select('*')
        .eq('organisation_id', row.organisation_id)
        .eq('lifecycle_ref', row.lifecycle_ref)
        .maybeSingle(),
      accountsService.get(row.account_id),
      accountInvoiceConfig(row.organisation_id),
    ]);
    return {
      invoice: toView(row),
      arrangement: arrangementResult.data ? fromDbArrangement(arrangementResult.data) : null,
      account,
      config,
      organisationId: String(row.organisation_id),
    };
  },

  /** Draft only: issue date, terms, the line wording and the notes. Amounts come from the arrangement. */
  async updateDraft(id: string, patch: {
    issueDate: string;
    paymentTermsInDays: number;
    lineId?: string | null;
    lineName?: string | null;
    notes?: string | null;
  }): Promise<void> {
    const row = await loadRow(id);
    if (normaliseInvoiceStatus(row.status) !== 'draft') throw new Error('Only draft invoices can be edited.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(patch.issueDate)) throw new Error('Enter the invoice date.');
    if (!(Number.isInteger(patch.paymentTermsInDays) && patch.paymentTermsInDays >= 0)) {
      throw new Error('Payment terms must be a whole number of days.');
    }
    const { error } = await supabase
      .from('documents')
      .update({
        issued_at: dateOnlyToIssuedAt(patch.issueDate),
        payment_terms_in_days: patch.paymentTermsInDays,
        notes: String(patch.notes || '').trim() || null,
      })
      .eq('id', id);
    if (error) throw new Error(error.message);
    const name = String(patch.lineName || '').trim();
    if (patch.lineId && name) {
      const { error: lineError } = await supabase.from('document_lines').update({ name }).eq('id', patch.lineId);
      if (lineError) throw new Error(lineError.message);
    }
  },

  /**
   * Draft → sent, sent → paid, sent → void. Sent and paid also go to FreeAgent automatically
   * when FreeAgent sync is on, as for every invoice.
   */
  async setStatus(id: string, status: AccountInvoiceStatus): Promise<void> {
    const row = await loadRow(id);
    const current = normaliseInvoiceStatus(row.status);
    const allowed = (current === 'draft' && status === 'sent')
      || (current === 'sent' && (status === 'paid' || status === 'void'));
    if (!allowed) throw new Error(`A ${current} invoice cannot be marked ${status}.`);
    await documentsService.updateDocument(id, { status });
    if (status === 'void') {
      await releaseRecurringPeriod(row);
      if (row.freeagent_invoice_url) {
        try {
          await freeAgentApi.markInvoiceCancelled(row.freeagent_invoice_url);
        } catch (err) {
          throw new Error(`The invoice is void in JARVIS, but FreeAgent could not cancel it: ${err instanceof Error ? err.message : String((err as any)?.message || err)}. Cancel it in FreeAgent.`);
        }
      }
    }
  },

  /** Deletes a draft. A recurring period it billed becomes due again. */
  async remove(id: string): Promise<void> {
    const row = await loadRow(id);
    if (normaliseInvoiceStatus(row.status) !== 'draft') throw new Error('Only draft invoices can be deleted. Void a sent invoice instead.');
    if (row.freeagent_invoice_url) throw new Error('This invoice is already in FreeAgent. Void it instead.');
    await documentsService.deleteDocument(id);
    await releaseRecurringPeriod(row);
  },

  /** Overrides the invoice number. Allowed until the invoice is paid, void or in FreeAgent. */
  async changeRef(id: string, value: string): Promise<string> {
    const problem = validateInvoiceRef(value);
    if (problem) throw new Error(problem);
    const row = await loadRow(id);
    if (!canChangeInvoiceRef(normaliseInvoiceStatus(row.status), row.freeagent_invoice_url)) {
      throw new Error('The number can only change while the invoice is a draft or sent, and not yet in FreeAgent.');
    }
    const ref = value.trim();
    const { error } = await supabase
      .from('documents')
      .update({ invoice_ref: ref, ref_no: ref })
      .eq('id', id);
    if (error) {
      if (error.code === '23505') throw new Error(`${ref} is already used by another invoice.`);
      if (/immutable/i.test(error.message)) {
        throw new Error('The database does not allow this change yet. Apply the account invoice ref override migration.');
      }
      throw new Error(error.message);
    }
    return ref;
  },

  async sendToFreeAgent(id: string, userId?: string): Promise<SyncResult> {
    return resyncInvoiceToFreeAgent(id, userId, { source: 'account_invoice' });
  },

  /** Renders the organisation's account invoice template (falling back to the built-in layout) as a PDF. */
  async pdfBlob(loaded: LoadedAccountInvoice): Promise<Blob> {
    // Account invoices have their own template; before it exists, the brand's Invoice template was the account layout
    const saved = await documentTemplateService.getTemplate(loaded.organisationId, 'AccountInvoice').catch(() => null)
      || await documentTemplateService.getTemplate(loaded.organisationId, 'Invoice').catch(() => null);
    const templateHtml = typeof saved?.template === 'string' && saved.template.length > 0
      ? saved.template
      : KSU_INVOICE_TEMPLATE;
    const { html, pdfOptions } = renderAccountInvoicePdfRequest({
      templateHtml,
      invoice: loaded.invoice,
      account: loaded.account,
      arrangement: loaded.arrangement,
      config: loaded.config,
      origin: window.location.origin,
      imageAspects: {
        header: await imageAspect(loaded.config?.headerImagePath),
        footer: await imageAspect(loaded.config?.footerImagePath),
      },
    });
    const response = await fetch('/api/export-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, pdfOptions }),
    });
    if (!response.ok) {
      const message = await response.text().catch(() => '');
      throw new Error(message || `PDF export failed (${response.status})`);
    }
    return response.blob();
  },

  pdfFileName(loaded: LoadedAccountInvoice): string {
    return `${loaded.invoice.invoiceRef || 'invoice'}.pdf`;
  },

  async downloadPdf(loaded: LoadedAccountInvoice): Promise<void> {
    const blob = await this.pdfBlob(loaded);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.pdfFileName(loaded);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
